import type { PieCompositionCapabilities } from '../../../chart-components/unified-pie-composition-chart/unified-pie-composition-chart.types';
import { toNumberScale } from '../../dvm-layout-builder/dvm-builder-number-format.utils';

/**
 * Fold the Format accordion / tile Format popup values into a pie-composition
 * capabilities object. Pure: used by the step at Generate / preview time and
 * by the shell when the Format popup of an existing tile changes. Mirrors
 * `applyScatterBubbleFormatToCapabilities`; this family has NO axis keys.
 */
export function applyPieCompositionFormatToCapabilities(
  capabilities: PieCompositionCapabilities,
  format: Record<string, unknown> | undefined,
  resolvePaletteColors: (paletteKey?: string) => string[] | undefined,
): PieCompositionCapabilities {
  const f = format || {};
  const num = (key: string): number | undefined =>
    f[key] !== undefined && f[key] !== '' && f[key] !== null && Number.isFinite(Number(f[key])) ? Number(f[key]) : undefined;

  // Legend.
  capabilities.showLegend = f['showLegend'] !== false;
  capabilities.legendPosition = f['legendPosition'] ? String(f['legendPosition']) : undefined;
  // Labels.
  capabilities.showValues = f['showValues'] !== false;
  capabilities.labelPosition = f['labelPosition'] ? String(f['labelPosition']) : undefined;
  capabilities.labelFontSize = num('labelFontSize');
  capabilities.labelColor = (f['labelColor'] as string) || undefined;
  // Tooltip.
  capabilities.tooltipEnabled = f['tooltipEnabled'] !== false;
  capabilities.tooltipTrigger = f['tooltipTrigger'] ? String(f['tooltipTrigger']) : undefined;
  capabilities.tooltipPosition = f['tooltipPosition'] ? String(f['tooltipPosition']) : undefined;
  capabilities.tooltipBackground = (f['tooltipBackground'] as string) || undefined;
  capabilities.tooltipTextColor = (f['tooltipTextColor'] as string) || undefined;
  capabilities.tooltipBorderColor = (f['tooltipBorderColor'] as string) || undefined;
  capabilities.tooltipBorderRadius = num('tooltipBorderRadius');
  capabilities.tooltipFontSize = num('tooltipFontSize');
  // Formatting.
  capabilities.palette = f['palette'] ? String(f['palette']) : undefined;
  capabilities.paletteColors = resolvePaletteColors(capabilities.palette);
  capabilities.colors = undefined;
  capabilities.numberFormat = String(f['numberFormat'] ?? 'NONE');
  capabilities.numberScale = f['numberScale'] ? String(f['numberScale']) : toNumberScale(f['numberFormat']);
  capabilities.decimals = typeof f['decimals'] === 'number' ? (f['decimals'] as number) : Number(f['decimals'] ?? 1);
  capabilities.innerRadius = (f['innerRadius'] as string) || undefined;
  capabilities.outerRadius = (f['outerRadius'] as string) || undefined;
  capabilities.colorByMeasure = f['colorByMeasure'] === true || capabilities.colorByMeasure === true;
  // Hierarchy — inert unless the active variant is Sunburst / Treemap / Nested Donut.
  capabilities.drillDownEnabled = f['drillDownEnabled'] !== false && capabilities.drillDownEnabled !== false;
  // Progress — inert unless the active variant is a gauge style.
  capabilities.roundCap = f['roundCap'] !== false;
  capabilities.trackColor = (f['trackColor'] as string) || undefined;
  capabilities.trackWidth = num('trackWidth');
  capabilities.targetValue = num('targetValue');
  // Interaction.
  capabilities.highlightOnClick = f['highlightOnClick'] === true;
  capabilities.crossFilter = f['crossFilter'] === true;
  capabilities.emitSelection = f['emitSelection'] === true;
  // Advanced.
  capabilities.sortBy = f['sortBy'] ? String(f['sortBy']) : undefined;
  capabilities.topN = num('topN');
  capabilities.animation = f['animation'] !== false;
  return capabilities;
}
